import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import swal from 'sweetalert';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import Button from '../../components/bootstrap/Button';
import { getLink, links } from '../../helpers/urls';
import { authMenu } from '../../menu';
import Spinner from '../../components/bootstrap/Spinner';

const LoginHeader = () => {
	return (
		<>
			<div className='text-center h1 fw-bold mt-5'>Create Account,</div>
			<div className='text-center h4 text-muted mb-5'>Sign up to get started!</div>
		</>
	);
};

const SignUpSchema = Yup.object().shape({
	first_name: Yup.string().required('This field is required'),
	last_name: Yup.string().required('This field is required'),
	email: Yup.string().email('Please enter a valid email').required('This field is required'),
	password: Yup.string()
		.required('This field is required')
		.matches(
			// eslint-disable-next-line no-useless-escape
			/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
			'Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character',
		),
	mobile: Yup.string()
		.required('This field is required')
		.matches(/^[0]\d{10}$/, 'Please enter a valid phone number'),
	password_confirmation: Yup.string()
		.oneOf([Yup.ref('password')], 'Passwords must match')
		.required('This field is required'),
});

async function registerUser(credentials) {
	return fetch(getLink(links.pages.register), {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify(credentials),
	}).then((data) => data.json());
}

const Registration = () => {
	const [loading, setLoading] = useState(false);

	const navigate = useNavigate();

	const formik = useFormik({
		initialValues: {
			first_name: '',
			last_name: '',
			other_name: '',
			user_type: 'agent',
			email: '',
			mobile: '',
			password: '',
			password_confirmation: '',
		},
		validationSchema: SignUpSchema,
		onSubmit: async (values) => {
			setLoading(true);
			delete values.password_confirmation;
			const response = await registerUser(values);
			setLoading(false);
			if (response.status) {
				navigate(authMenu.confirmSignUp.path, {
					state: {
						email: response.data.email,
						user_id: response.data.id,
						password: values.password,
					},
				});
			} else if (response.message.email) {
				swal('Oops!', response.message.email[0], 'error');
			} else if (response.message.mobile) {
				swal('Oops!', response.message.mobile[0], 'error');
			} else {
				swal('Oops!', response.message, 'error');
			}
		},
	});

	return (
		<>
			<LoginHeader />

			<form className='row g-4' noValidate onSubmit={formik.handleSubmit} method='POST'>
				<div className='row'>
					<div className='col-md-12 mb-3'>
						<FormGroup id='name' isFloating label='First name'>
							<Input
								type='text'
								name='first_name'
								disabled={loading}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								value={formik.values.first_name}
								isTouched={formik.touched.first_name}
								// eslint-disable-next-line no-unneeded-ternary
								isValid={formik.errors.first_name !== null ? true : false}
								invalidFeedback={formik.errors.first_name}
							/>
						</FormGroup>
						{formik.errors.first_name && formik.touched.first_name && (
							<div className='text-danger text-sm mt-2'>
								{formik.errors.first_name}
							</div>
						)}
					</div>
					<div className='col-md-12 mb-3'>
						<FormGroup id='lname' isFloating label='Last name'>
							<Input
								type='text'
								name='last_name'
								disabled={loading}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								value={formik.values.last_name}
								isTouched={formik.touched.last_name}
								// eslint-disable-next-line no-unneeded-ternary
								isValid={formik.errors.last_name !== null ? true : false}
								invalidFeedback={formik.errors.last_name}
							/>
						</FormGroup>
						{formik.errors.last_name && formik.touched.last_name && (
							<div className='text-danger text-sm mt-2'>
								{formik.errors.last_name}
							</div>
						)}
					</div>
					<div className='col-md-12 mb-3'>
						<FormGroup id='oname' isFloating label='Other name'>
							<Input
								type='text'
								name='other_name'
								disabled={loading}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								value={formik.values.other_name}
								isTouched={formik.touched.other_name}
							/>
						</FormGroup>
					</div>
					<div className='col-md-8 mb-3'>
						<FormGroup id='email' isFloating label='Email address'>
							<Input
								type='email'
								name='email'
								disabled={loading}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								value={formik.values.email}
								isTouched={formik.touched.email}
								// eslint-disable-next-line no-unneeded-ternary
								isValid={formik.errors.email !== null ? true : false}
								invalidFeedback={formik.errors.email}
							/>
						</FormGroup>
						{formik.errors.email && formik.touched.email && (
							<div className='text-danger text-sm mt-2'>{formik.errors.email}</div>
						)}
					</div>
					<div className='col-md-4 mb-3'>
						<FormGroup id='phone' isFloating label='Phone number'>
							<Input
								type='text'
								name='mobile'
								required
								disabled={loading}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								value={formik.values.mobile}
								isTouched={formik.touched.mobile}
								// eslint-disable-next-line no-unneeded-ternary
								isValid={formik.errors.mobile !== null ? true : false}
								invalidFeedback={formik.errors.mobile}
							/>
						</FormGroup>
						{formik.errors.mobile && formik.touched.mobile && (
							<div className='text-danger text-sm mt-2'>{formik.errors.mobile}</div>
						)}
					</div>
					<div className='col-md-6 mb-3'>
						<FormGroup id='password' isFloating label='Password'>
							<Input
								type='password'
								name='password'
								required
								disabled={loading}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								value={formik.values.password}
								isTouched={formik.touched.password}
								// eslint-disable-next-line no-unneeded-ternary
								isValid={formik.errors.password !== null ? true : false}
								invalidFeedback={formik.errors.password}
							/>
						</FormGroup>
						{formik.errors.password && formik.touched.password && (
							<div className='text-danger text-sm mt-2'>{formik.errors.password}</div>
						)}
					</div>
					<div className='col-md-6 mb-3'>
						<FormGroup
							id='password_confirmation'
							isFloating
							label='Password Confirmation'>
							<Input
								type='password'
								name='password_confirmation'
								required
								disabled={loading}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								value={formik.values.password_confirmation}
								isTouched={formik.touched.password_confirmation}
								// eslint-disable-next-line no-unneeded-ternary
								isValid={formik.errors.password_confirmation !== null}
								invalidFeedback={formik.errors.password_confirmation}
							/>
						</FormGroup>
						{formik.errors.password_confirmation &&
							formik.touched.password_confirmation && (
								<div className='text-danger text-sm mt-2'>
									{formik.errors.password_confirmation}
								</div>
							)}
					</div>
					<div className='col-md-12 text-center'>
						{loading ? (
							<Button type='submit' color='light' disabled={loading} className='py-3'>
								<Spinner color='info'>Loading...</Spinner>
							</Button>
						) : (
							<Button
								type='submit'
								color='info'
								className='w-100 py-3 '
								disabled={loading}>
								Sign Up
							</Button>
						)}
					</div>
				</div>
			</form>
		</>
	);
};

export default Registration;
